import { CurrencyService } from "../services/CurrencyService";
import { TrvCryptoService } from "../services/TrvCryptoService";
import { formatDate, formatMoney, formatPercent } from "../utils/formatters";

export class PriceStatistics {
  private wrapper: JQuery<HTMLDivElement>;
  private button: JQuery<HTMLAnchorElement>;
  private isOpen = false;
  private static WRAPPER_OPEN_CLASS = "price-statistics-module-wrapper-open";
  private static readonly LOADING_CLASS = "price-statistics-loading";

  constructor() {
    this.wrapper = $(".price-statistics-module-wrapper");
    this.button = this.wrapper.find<HTMLAnchorElement>(
      ".price-statistics-button"
    );
    this.wrapper.addClass(PriceStatistics.LOADING_CLASS);

    this.bindEvents();
    this.getData();
  }

  private bindEvents() {
    this.button.on("click", this.onButtonClick.bind(this));
  }

  private onButtonClick(e: JQuery.ClickEvent) {
    e.preventDefault();
    this.toggle();
  }

  private async getData() {
    const currency = await CurrencyService.getSelected();
    if (!currency?.code) {
      return;
    }
    const statistics = (
      currency!!.code
        ? (await TrvCryptoService.getStatistics(currency.code)) || {}
        : {}
    ) as CryptoStatistics;

    this.renderItems(statistics);
  }

  private renderItems(statistics: CryptoStatistics) {
    const rows: PriceStatisticsListItem[] = [];

    rows.push({
      textSmall: `${statistics.name} Preço Hoje`,
      label: `Preço de ${statistics.name || ""}`,
      value: formatMoney(statistics.price || 0),
    });
    rows.push({
      label: "Alternância de Preço",
      value: formatMoney(statistics.priceChange24h || 0),
      variation: statistics.priceChange24hPercent || 0,
    });
    rows.push({
      label: "Baixas nas 24h / Altas nas 24h",
      value: `${formatMoney(statistics.priceLow24h || 0, "BRL")}/ ${formatMoney(
        statistics.priceHigh24h || 0,
        "BRL"
      )}`,
    });
    rows.push({
      label: "Volume de Trading",
      value: formatMoney(statistics.tradingVolume24 || 0),
      variation: statistics.tradingVolume24Percent || 0,
    });
    rows.push({
      label: "Volume / Capitalização de Mercado",
      value: statistics.volumeMarketCap || 0,
    });
    rows.push({
      label: "Market Dominance",
      value: formatPercent(statistics.marketDominance || 0),
    });
    rows.push({
      label: "Market Rank",
      value: `#${statistics.marketRank || 0}`,
    });

    rows.push({
      textSmall: `${statistics.name} Market Cap`,
      label: "Market Cap",
      value: formatMoney(statistics.marketCap || 0),
    });
    rows.push({
      label: "Fully Diluted Market Cap",
      value: formatMoney(statistics.fullyDilutedMarketCap || 0),
      variation: statistics.fullyDilutedMarketCapPercent || 0,
    });

    if (statistics.priceYesterdayLow && statistics.priceYesterdayHigh) {
      rows.push({
        textSmall: `${statistics.name} Preço ontem`,
        label: "Baixas ontem / Altas ontem",
        value: `${formatMoney(statistics.priceYesterdayLow)}/ ${formatMoney(
          statistics.priceYesterdayHigh
        )}`,
      });
    }
    rows.push({
      label: "Alternancia de preço ontem",
      value: formatMoney(statistics.priceChangeYesterdayPercent || 0),
    });
    rows.push({
      label: "Volume ontem",
      value: formatMoney(statistics.volumeYesterday || 0),
    });

    rows.push({
      label: "Alternancia de preço 7 dias",
      value: formatPercent(statistics.priceLow7d || 0),
    });
    rows.push({
      label: "Alternancia de preço 30 dias",
      value: formatPercent(statistics.priceLow30d || 0),
    });
    rows.push({
      label: "Maior baixa",
      value: formatMoney(statistics.priceLowAllTime || 0),
      variation: statistics.priceLowAllTimePercent || 0,
    });
    rows.push({
      label: "Maior alta",
      value: formatMoney(statistics.priceHighAllTime || 0),
      variation: statistics.priceHighAllTimePercent || 0,
    });
    rows.push({ label: "ROI", value: formatPercent(statistics.roi || 0) });

    rows.push({
      textSmall: `${statistics.name} Supply`,
      label: "Circulating Supply",
      value: formatMoney(statistics.circulatingSupply || 0, "BTC"),
    });
    rows.push({
      label: "Total Supply",
      value: formatMoney(statistics.totalSupply || 0, "BTC"),
    });
    rows.push({
      label: "Max Supply",
      value: formatMoney(statistics.maxSupply || 0, "BTC"),
    });

    const $items = rows
      .map(this.getRowHtml)
      .filter(($row) => $row !== undefined) as JQuery<HTMLElement>[];

    const lastUpdate = $("<li>", {
      class: "price-statistics-item",
      html: [
        $("<span>", {
          class: "price-statistics-text-small",
          html: `Atualizado em ${formatDate(
            statistics.lastUpdateTimestamp || 0
          )}`,
        }),
      ],
    });

    this.wrapper.find(".price-statistics-currency").text(statistics.symbol);

    this.wrapper
      .find(".price-statistics-list")
      .html("")
      .append($items)
      .append(lastUpdate);

    this.wrapper.removeClass(PriceStatistics.LOADING_CLASS);
  }

  private getRowHtml(
    item: PriceStatisticsListItem
  ): JQuery<HTMLElement> | undefined {
    const $li = $("<li>", { class: "price-statistics-item" });
    const $row = ($content: JQuery<HTMLElement> | JQuery<Element>[]) =>
      $("<div>", { class: "price-statistics-row", html: $content });

    if (item.value === undefined) return;
    if (item.textSmall)
      $li.append(
        $row(
          $("<span>", {
            class: "price-statistics-text-small",
            html: item.textSmall || "",
          })
        )
      );

    $li.append(
      $row([
        $("<span>", { class: "price-statistics-text", html: item.label || "" }),
        $("<span>", { class: "price-statistics-text", html: item.value || "" }),
      ])
    );

    if (item.variation) {
      const $variation = $("<span>", {
        class:
          item.variation!! > 0
            ? "price-statistics-text-green"
            : "price-statistics-text-red",
        html: formatPercent(item.variation || 0),
      });

      $li.append($row([$("<span>"), $variation]));
    }

    return $li;
  }

  toggle() {
    this.wrapper.toggleClass(PriceStatistics.WRAPPER_OPEN_CLASS);
    this.button.text(
      this.isOpen
        ? this.button.data("view-more-text")
        : this.button.data("view-less-text")
    );
    this.isOpen = !this.isOpen;
  }
}

interface PriceStatisticsListItem {
  textSmall?: string;
  label: string;
  value?: string | number;
  variation?: number;
}
